import { useMemo, useState } from 'react'
import { useOrganizationMembers, useUpdateMemberRole, useRemoveMember } from '../../hooks/useOrganization'
import type { OrganizationMember } from '../../hooks/useOrganization'
import { useAuth } from '../../auth/AuthContext'
import { InviteMemberModal } from './InviteMemberModal'

export function getRoleBadgeClass(role: string) {
  switch (role) {
    case 'admin':
      return 'badge badge-accent'
    case 'editor':
      return 'badge badge-info'
    case 'viewer':
      return 'badge badge-neutral'
    default:
      return 'badge badge-neutral'
  }
}

export function getRoleLabel(role: string) {
  switch (role) {
    case 'admin':
      return '管理者'
    case 'editor':
      return '編集者'
    case 'viewer':
      return '閲覧者'
    default:
      return role
  }
}

type SortField = 'name' | 'role' | 'status' | 'joinedAt'
type SortOrder = 'asc' | 'desc'

function joinedAtMillis(member: OrganizationMember): number {
  const v = member.joinedAt
  if (!v) return 0
  if (typeof v.toDate === 'function') return v.toDate().getTime()
  const d = new Date(v)
  return isNaN(d.getTime()) ? 0 : d.getTime()
}

const ROLE_ORDER: Record<string, number> = { admin: 0, editor: 1, viewer: 2 }

export function OrganizationMembersSection() {
  const { data: members, isLoading: membersLoading } = useOrganizationMembers()
  const { user } = useAuth()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const updateMemberRole = useUpdateMemberRole()
  const removeMember = useRemoveMember()

  // デフォルト: 登録日（参加日）の降順
  const [sortField, setSortField] = useState<SortField>('joinedAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder(field === 'joinedAt' ? 'desc' : 'asc')
    }
  }

  const sortedMembers = useMemo(() => {
    if (!members) return []
    const list = [...members]
    const dir = sortOrder === 'asc' ? 1 : -1
    list.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'name':
          cmp = (a.displayName || a.email).localeCompare(b.displayName || b.email, 'ja')
          break
        case 'role':
          cmp = (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
          break
        case 'status':
          cmp = (a.status || '').localeCompare(b.status || '')
          break
        case 'joinedAt':
          cmp = joinedAtMillis(a) - joinedAtMillis(b)
          break
      }
      return cmp * dir
    })
    return list
  }, [members, sortField, sortOrder])

  const SortIcon = ({ field }: { field: SortField }) => {
    const style = { width: 14, height: 14, flexShrink: 0 } as const
    if (sortField !== field) {
      return (
        <svg style={style} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortOrder === 'asc' ? (
      <svg style={{ ...style, color: 'var(--color-accent)' }} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg style={{ ...style, color: 'var(--color-accent)' }} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    )
  }

  const SortableTh = ({ field, label }: { field: SortField; label: string }) => (
    <th className="sortable" onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span className="flex items-center gap-1">
        {label}
        <SortIcon field={field} />
      </span>
    </th>
  )

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 className="section-title">メンバー一覧</h2>
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-1)' }}>
            組織に所属しているメンバーの一覧です
          </p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            メンバーを招待
          </button>
        )}
      </div>

      {membersLoading ? (
        <div className="card text-center" style={{ padding: 'var(--space-12)' }}>
          <span className="spinner lg" style={{ margin: '0 auto' }} />
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-3)' }}>読み込み中...</p>
        </div>
      ) : sortedMembers.length > 0 ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <SortableTh field="name" label="メンバー" />
                <SortableTh field="role" label="ロール" />
                <SortableTh field="status" label="ステータス" />
                <SortableTh field="joinedAt" label="登録日" />
                {user?.role === 'admin' && <th>アクション</th>}
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((member) => (
                <tr key={member.uid}>
                  <td>
                    <div className="contact-cell">
                      <span className="avatar avatar-md avatar-accent">
                        {member.displayName?.charAt(0) || member.email.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <span className="contact-name">
                          {member.displayName || 'ユーザー'}
                          {member.uid === user?.uid && (
                            <span className="text-xs text-tertiary" style={{ marginLeft: 'var(--space-2)', fontWeight: 400 }}>(あなた)</span>
                          )}
                        </span>
                        <span className="text-sm text-secondary">{member.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(member.role)}>
                      {getRoleLabel(member.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${member.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                      {member.status === 'active' ? 'アクティブ' : '非アクティブ'}
                    </span>
                  </td>
                  <td>
                    {member.joinedAt ? (
                      typeof member.joinedAt.toDate === 'function'
                        ? new Date(member.joinedAt.toDate()).toLocaleDateString('ja-JP')
                        : new Date(member.joinedAt).toLocaleDateString('ja-JP')
                    ) : '-'}
                  </td>
                  {user?.role === 'admin' && (
                    <td>
                      {member.uid !== user?.uid ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={member.role}
                            onChange={async (e) => {
                              if (confirm(`${member.email}のロールを${e.target.value}に変更しますか？`)) {
                                try {
                                  await updateMemberRole.mutateAsync({
                                    memberId: member.uid,
                                    role: e.target.value as 'admin' | 'editor' | 'viewer'
                                  })
                                  alert('ロールを変更しました')
                                } catch (error) {
                                  alert(error instanceof Error ? error.message : 'ロールの変更に失敗しました')
                                }
                              }
                            }}
                            className="form-select"
                            style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--font-size-sm)' }}
                          >
                            <option value="viewer">閲覧者</option>
                            <option value="editor">編集者</option>
                            <option value="admin">管理者</option>
                          </select>
                          <button
                            onClick={async () => {
                              if (confirm(`${member.email}を削除しますか？`)) {
                                try {
                                  await removeMember.mutateAsync(member.uid)
                                  alert('メンバーを削除しました')
                                } catch (error) {
                                  alert(error instanceof Error ? error.message : 'メンバーの削除に失敗しました')
                                }
                              }
                            }}
                            className="btn btn-danger btn-sm"
                          >
                            削除
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-tertiary">-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card text-center" style={{ padding: 'var(--space-12)' }}>
          <span className="icon-wrap icon-wrap-xl icon-wrap-neutral" style={{ margin: '0 auto' }}>
            <svg style={{ width: 24, height: 24 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </span>
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-3)' }}>メンバーが見つかりません</p>
        </div>
      )}

      {/* メンバー招待モーダル */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </div>
  )
}
